/**
 * Solutions configs — iter 173.
 *
 * Audience-specific landing pages powered by the shared PillarPage. One
 * page per buyer archetype so each surface answers "why does this apply
 * to me" and paid + SEO traffic can route by role.
 */
import {
  ShieldCheck, Bot, ClipboardCheck, LineChart, Users, Inbox,
  Gauge, Building2, Hospital, Timer, Stethoscope, HeartPulse,
} from 'lucide-react';

const primaryCta = { label: 'Request a demo', href: '/contact?type=demo' };
const providerCta = { label: 'Talk to us', href: '/contact?type=provider' };

// /solutions/hospitals
export const solutionsHospitalsConfig = {
  slug: 'solutions/hospitals',
  title: 'CaraLoom for Hospitals',
  metaTitle: 'CaraLoom for Hospitals — Discharge Planning & Post-Acute Referrals',
  metaDescription:
    'Hospital-grade discharge planning and post-acute referral management. Reduce length of stay, standardize patient choice, and coordinate every level of post-acute care across a single platform.',
  keywords: [
    'hospital discharge planning software',
    'hospital case management software',
    'reduce hospital length of stay',
    'post acute referral platform for hospitals',
    'patient placement software',
  ],
  eyebrow: 'For Hospitals',
  h1: 'The post-acute layer your case managers can actually run.',
  subhead:
    'Turn a discharge order into a placed patient without spreadsheets, fax queues, or phone tree marathons — with the audit trail and length-of-stay analytics your CFO and compliance leaders expect.',
  heroSupport:
    'Deploy hospital-wide in weeks. Integrate with Epic, Cerner/Oracle Health, MEDITECH, and Athenahealth via FHIR + HL7. No PHI required to see a demo.',
  primaryCta,
  secondaryCta: { label: 'How planners work in CaraLoom', href: '/for-discharge-planners' },
  benefitsHeading: 'What hospitals get from CaraLoom',
  benefits: [
    { icon: Timer, k: 'Faster placement', title: 'Cut days out of your discharge loop',
      body: 'Automated matching, in-loop coverage checks, and digital signatures compress the referral-to-placement window into hours.' },
    { icon: Inbox, k: 'Exception queue', title: 'A single case-manager inbox',
      body: 'Coverage holds, expired offers, unsigned documents, and escalations — one prioritized queue with the exact next action per card.' },
    { icon: Users, k: 'Documented patient choice', title: 'CMS discharge-planning ready',
      body: 'Patients and families see the same qualifying provider list; choice is stamped into the record. Compliance reviews shrink from weeks to hours.' },
    { icon: LineChart, k: 'Avoidable-day analytics', title: 'The metrics leadership acts on',
      body: 'Time-to-placement, decline reasons, avoidable days by unit / service line / payer. Data your COO can read and your team can improve.' },
    { icon: ClipboardCheck, k: 'Auditable', title: 'HIPAA-grade audit on every action',
      body: 'Every referral, override, decline, and hand-off is time-stamped with actor and reason. Compliance-by-default, not bolt-on.' },
    { icon: Building2, k: 'System-wide rollup', title: 'One platform across every hospital',
      body: 'Compare access, placement, and network utilization across every hospital in your system — from a single dashboard.' },
  ],
  workflowHeading: 'How a hospital rolls out CaraLoom',
  workflow: [
    { n: '1', title: 'Integrate', body: 'FHIR/HL7 connection to your EHR, secure eFax ingestion, and SSO for your case-manager team. Typical time-to-live: 2\u20134 weeks.' },
    { n: '2', title: 'Verify network', body: 'Import your preferred post-acute network. Every provider is verified for capability, service area, and payer acceptance — no fabricated data.' },
    { n: '3', title: 'Train the exception queue', body: 'Case managers move from spreadsheets and fax cover sheets to a single inbox with prioritized actions.' },
    { n: '4', title: 'Turn on analytics', body: 'Length-of-stay, decline reasons, and network utilization roll up automatically. Your CFO gets the report they always wanted.' },
    { n: '5', title: 'Scale to system', body: 'Once one hospital is live, roll out horizontally to every hospital in the system on the same tenancy model.' },
  ],
  audienceHeading: 'Who works inside CaraLoom on the hospital side',
  audiencePaths: [
    { title: 'Case managers', body: 'One queue, one action per card.', cta: 'See the workflow', href: '/for-discharge-planners' },
    { title: 'CFO / VP finance', body: 'Turn avoidable days into a KPI you can actually move.', cta: 'See the analytics', href: '/care-transitions' },
    { title: 'CIO / IT', body: 'API-first, HIPAA-grade, SSO-ready.', cta: 'See integrations', href: '/trust' },
  ],
  faq: [
    { q: 'How does CaraLoom fit alongside our EHR?',
      a: 'CaraLoom lives beside Epic, Cerner/Oracle Health, MEDITECH, or Athenahealth via FHIR + HL7. It owns the referral, patient-choice, and post-acute-tracking surface — your EHR remains the clinical documentation system of record.' },
    { q: 'How is this different from CarePort?',
      a: 'CaraLoom is oriented around the case-manager\u2019s exception queue, treats payer coverage as a first-class step in the loop, and is API-first for health-system integration teams. Feature-level comparisons live at /compare/caraloom-vs-careport.' },
    { q: 'What does implementation look like?',
      a: 'Typical hospital deployment is 2\u20134 weeks: EHR integration + preferred-network import + case-manager training + analytics turn-on. Multi-hospital systems roll out sequentially on the same tenancy.' },
  ],
  softwareFeatures: [
    'FHIR + HL7 EHR integration',
    'Post-acute network management',
    'CMS-compliant patient choice',
    'Automated referral matching + offer waves',
    'Length-of-stay analytics',
    'HIPAA audit trail',
  ],
  bottomCtaTitle: 'See CaraLoom running on your data model',
  bottomCtaBody: 'A 30-minute demo with your discharge disposition mix in mind. No PHI required.',
};

// /solutions/health-systems
export const solutionsHealthSystemsConfig = {
  slug: 'solutions/health-systems',
  title: 'CaraLoom for Health Systems',
  metaTitle: 'CaraLoom for Health Systems — Multi-Hospital Post-Acute Coordination',
  metaDescription:
    'Coordinate post-acute care across every hospital in your system. Standardize network utilization, reduce avoidable days, and make length-of-stay a KPI your leadership can actually move.',
  keywords: [
    'health system discharge planning',
    'multi hospital case management',
    'post acute network management',
    'system wide care transitions',
    'health system post acute platform',
  ],
  eyebrow: 'For Health Systems',
  h1: 'One post-acute layer across every hospital in your system.',
  subhead:
    'Standardize discharge planning, own your preferred network, and roll up access and length-of-stay data into a single dashboard your executive team can act on.',
  heroSupport: 'Tenanted per hospital. Rolled up per system. Nothing standardized at the cost of hospital-level flexibility.',
  primaryCta,
  secondaryCta: { label: 'See the platform', href: '/post-acute-care-platform' },
  benefitsHeading: 'What changes when a system moves onto CaraLoom',
  benefits: [
    { icon: Building2, k: 'System rollup', title: 'One dashboard for every hospital',
      body: 'Access, placement, and utilization data roll up across the system without a data warehouse project.' },
    { icon: LineChart, k: 'Network intelligence', title: 'Data-driven contracting',
      body: 'See which post-acute partners actually accept your patients, at what speed, with what decline patterns — instead of anecdotes.' },
    { icon: Hospital, k: 'Every hospital', title: 'Standardize without stifling',
      body: 'A shared platform with hospital-level tenancy — corporate sees the aggregate, individual hospitals keep their operational flexibility.' },
    { icon: HeartPulse, k: 'Readmission lens', title: '30-day readmission tracking',
      body: 'Every hand-off is tracked into the 30-day window; correlate placement decisions with readmission outcomes.' },
    { icon: Users, k: 'Patient choice at scale', title: 'CMS-ready across the system',
      body: 'System-wide policy for patient choice, applied consistently. Every hospital, every unit, every payer.' },
    { icon: ShieldCheck, k: 'Compliance', title: 'HIPAA + audit across tenants',
      body: 'System-level audit visibility with tenant scoping so each hospital keeps its own data plane.' },
  ],
  workflowHeading: 'How a system rolls out CaraLoom',
  workflow: [
    { n: '1', title: 'Pilot hospital',  body: 'Start with one hospital. Prove out integrations, network import, and case-manager workflow.' },
    { n: '2', title: 'Standardize',     body: 'Codify preferred network, patient-choice policy, and referral escalation rules at the system level.' },
    { n: '3', title: 'System rollout',  body: 'Extend to every hospital under the same tenancy model. Each hospital keeps operational flexibility.' },
    { n: '4', title: 'System dashboard',body: 'Turn on the executive dashboard for access, placement, and length-of-stay across the entire system.' },
    { n: '5', title: 'Continuous',      body: 'Feed outcome data back into network strategy and contracting decisions. Monthly, not annually.' },
  ],
  audiencePaths: [
    { title: 'System CMO / CNO', body: 'A single view of care transitions across every hospital.', cta: 'Request a demo', href: '/contact?type=demo' },
    { title: 'Network strategy', body: 'Data on which post-acute partners actually deliver.', cta: 'See analytics', href: '/care-transitions' },
    { title: 'Case management leaders', body: 'Standardize workflow without slowing individual hospitals.', cta: 'See the planner workflow', href: '/for-discharge-planners' },
  ],
  faq: [
    { q: 'Can each hospital keep its own workflow?',
      a: 'Yes. CaraLoom is tenanted per hospital under a system umbrella. Each hospital retains operational flexibility (preferred network, escalation rules, staffing model) while corporate rolls up standardized metrics across the system.' },
    { q: 'How does system-wide reporting work?',
      a: 'System-level users see aggregate access, placement, and utilization data across every tenant. Hospital-level users see only their own tenant. Both live on the same platform under a strict tenancy model.' },
    { q: 'Do you support system-wide preferred networks?',
      a: 'Yes. A preferred post-acute network can be defined at the system level and inherited by each hospital, with hospital-level overrides for geography.' },
  ],
  softwareFeatures: [
    'System-wide + hospital-level tenancy',
    'Preferred network management',
    'System dashboard for access + LOS + readmissions',
    'Standardized patient choice policy',
    'HIPAA audit trail with tenant scoping',
  ],
  bottomCtaTitle: 'One platform, every hospital',
  bottomCtaBody: 'A demo tailored to your system\u2019s hospital count, geography, and payer mix.',
};

// /solutions/case-managers
export const solutionsCaseManagersConfig = {
  slug: 'solutions/case-managers',
  title: 'CaraLoom for Case Managers',
  metaTitle: 'CaraLoom for Case Managers — The Discharge-Planning Home Base',
  metaDescription:
    'The case-manager\u2019s home base for discharge planning. One inbox for referrals, coverage holds, patient choice, and signed orders. Built with case managers, not just for them.',
  keywords: [
    'discharge planner software',
    'case manager workflow',
    'hospital case management platform',
    'discharge planning tools',
    'referral tracking for case managers',
  ],
  eyebrow: 'For Case Managers',
  h1: 'Your morning starts with what\u2019s stuck — not with a fax queue.',
  subhead:
    'One inbox for referrals, coverage holds, expired offers, unsigned documents, and escalations. Each card carries exactly one action. Nothing hides.',
  heroSupport: 'Designed with practicing hospital case managers. Every screen answers the question: what do I do next?',
  primaryCta: { label: 'Talk to case management leadership', href: '/contact?type=demo' },
  secondaryCta: { label: 'How the planner day flows', href: '/for-discharge-planners' },
  benefitsHeading: 'What a case-manager\u2019s day looks like on CaraLoom',
  benefits: [
    { icon: Inbox, k: 'Exception queue', title: 'One inbox — prioritized',
      body: 'Every case that needs attention is a card with a next action. No jumping between tabs, faxes, and spreadsheets.' },
    { icon: Bot, k: 'Automated matching', title: 'The system does the phone tree',
      body: 'Provider scoring, ranking, and offer waves run automatically — you see who was offered, when, and why.' },
    { icon: ShieldCheck, k: 'Coverage first', title: 'Eligibility runs before signature',
      body: 'A payer problem pauses the loop and asks you. It never fails silently. You never chase billing corrections.' },
    { icon: ClipboardCheck, k: 'Documented choice', title: 'CMS patient choice, done',
      body: 'Patients and families see the same ranked list; choice is documented with time-stamp and user. Ready for audit.' },
    { icon: Timer, k: 'Zero fax loops', title: 'F2F, 485, NOA, consent — signed in one thread',
      body: 'Orders draft when coverage clears. Signature is digital. No lost forms, no reprints, no "did they get it?" calls.' },
    { icon: LineChart, k: 'Your own scoreboard', title: 'See your caseload move',
      body: 'Your placement times, decline reasons, and open exceptions on one page. Advocate for your team with data.' },
  ],
  workflowHeading: 'A day in the CaraLoom exception queue',
  workflow: [
    { n: '1', title: 'Morning', body: 'Open the exception queue. Every case that needs attention is one card with the next action.' },
    { n: '2', title: 'Match',   body: 'For new referrals, review the ranked provider list. Send offer waves in one action.' },
    { n: '3', title: 'Coverage',body: 'Payer problems surface before signature. Resolve in-loop with your finance liaison.' },
    { n: '4', title: 'Choice',  body: 'Present the qualifying provider list to the patient. Their choice is documented on record.' },
    { n: '5', title: 'Track',   body: 'Every hand-off is time-stamped. You always know where every patient is in the loop.' },
  ],
  audiencePaths: [
    { title: 'Case management directors', body: 'Coach your team on data, not anecdotes.', cta: 'See the analytics', href: '/care-transitions' },
    { title: 'Bedside case managers', body: 'Move faster without losing accountability.', cta: 'See the workflow', href: '/for-discharge-planners' },
    { title: 'Utilization review', body: 'Coverage inside the loop instead of after the fact.', cta: 'See the platform', href: '/post-acute-care-platform' },
  ],
  faq: [
    { q: 'How is CaraLoom different from what I use today?',
      a: 'Most case managers today toggle between the EHR, a spreadsheet, an inbox, and a fax machine. CaraLoom collapses those into one exception queue with the next action per case, in-loop payer coverage, digital signatures, and documented patient choice — end to end.' },
    { q: 'How much training does the team need?',
      a: 'Case managers are typically productive in 1\u20132 hours; the workflow mirrors how experienced planners already think. Directors can be running dashboards on day one.' },
    { q: 'What happens when the machine picks wrong?',
      a: 'You override it. The system logs the override, the reason, and the outcome. Nothing final happens without a human signature. Machines do the phone tree; humans keep the call.' },
  ],
  softwareFeatures: [
    'Prioritized exception queue',
    'Automated offer waves + escalation',
    'In-loop coverage checking',
    'Patient choice capture',
    'Digital signature on F2F, 485, NOA, consent',
    'Individual + team scoreboards',
  ],
  bottomCtaTitle: 'See a case manager\u2019s day in CaraLoom',
  bottomCtaBody: 'A 30-minute walkthrough with a practicing planner. No PHI. No pitch deck.',
};

// /solutions/post-acute-providers
export const solutionsPostAcuteProvidersConfig = {
  slug: 'solutions/post-acute-providers',
  title: 'CaraLoom for Post-Acute Providers',
  metaTitle: 'CaraLoom for SNF, Home Health, Hospice, IRF, LTACH Providers',
  metaDescription:
    'Get referrals with verified coverage, clean documentation, and clear escalation rules. Show up in the right hospital searches. Manage your capacity and response signal on a single platform.',
  keywords: [
    'SNF referral platform',
    'home health referrals from hospitals',
    'hospice referral software',
    'IRF LTACH referrals',
    'post acute provider platform',
  ],
  eyebrow: 'For Post-Acute Providers',
  h1: 'Hospital referrals with the coverage already checked.',
  subhead:
    'CaraLoom is where hospital case managers search for you. Show up with verified capabilities, current response signal, and referrals that arrive with eligibility, authorization, and signed orders in the same thread.',
  heroSupport: 'Free to claim your profile. Verification is documented, not manufactured. No pay-to-play ranking.',
  primaryCta: providerCta,
  secondaryCta: { label: 'See the platform', href: '/post-acute-care-platform' },
  benefitsHeading: 'What providers get on CaraLoom',
  benefits: [
    { icon: Stethoscope, k: 'Cleaner referrals', title: 'Coverage already checked',
      body: 'Eligibility and authorization resolve before the referral reaches you. You accept a patient you can actually admit and bill for.' },
    { icon: Hospital, k: 'Right-sized volume', title: 'Referrals scoped to your capabilities',
      body: 'Verified capability, service area, and payer acceptance data mean you get referrals you can actually take — not everything a hospital sends into a fax queue.' },
    { icon: Users, k: 'Own your profile', title: 'Claim your provider record',
      body: 'Verified organizations manage their own profile: services, clinical capabilities, payers accepted, service area, contact routing.' },
    { icon: Timer, k: 'Response signal', title: 'Your accept-rate builds your ranking',
      body: 'Ranking is driven by objective response history — not by ad budget. Providers that accept quickly and reliably rise on their own merits.' },
    { icon: ClipboardCheck, k: 'One thread for orders', title: 'F2F, 485, NOA, consent — digital',
      body: 'No fax loops. No "did they get it?" calls. The whole order set lives on one thread with the referring case manager.' },
    { icon: LineChart, k: 'Own your data', title: 'See what you\u2019re winning and losing',
      body: 'Accept / decline history, referral sources, and volume by payer — a scoreboard for your business development team.' },
  ],
  workflowHeading: 'How a provider onboards to CaraLoom',
  workflow: [
    { n: '1', title: 'Claim',   body: 'Claim your profile on CaraLoom (free). We verify the organization matches the record.' },
    { n: '2', title: 'Verify',  body: 'Confirm services, clinical capabilities, payers accepted, and service area. No fabricated data.' },
    { n: '3', title: 'Route',   body: 'Set up intake routing — which team member receives referrals, in what format, from which referring facilities.' },
    { n: '4', title: 'Respond', body: 'Referrals arrive with coverage checked and clean documentation. Accept, decline, or escalate — in one action.' },
    { n: '5', title: 'Analyze', body: 'See your acceptance rate, response time, and referral source mix on your dashboard.' },
  ],
  audiencePaths: [
    { title: 'SNFs',        body: 'Referrals with verified coverage and clean documentation.',  cta: 'Claim your profile', href: '/contact?type=provider' },
    { title: 'Home health', body: 'Hospital referrals in a workflow you can staff around.',      cta: 'Claim your profile', href: '/contact?type=provider' },
    { title: 'Hospice',     body: 'Referrals with documented eligibility and family readiness.', cta: 'Claim your profile', href: '/contact?type=provider' },
    { title: 'IRF / LTACH', body: 'Show up in the right searches for the right complexity.',     cta: 'Claim your profile', href: '/contact?type=provider' },
    { title: 'Assisted living', body: 'Level-of-care matched referrals from hospital planners.', cta: 'Claim your profile', href: '/contact?type=provider' },
    { title: 'DME',         body: 'Order flow with prescription + eligibility in the same thread.', cta: 'Claim your profile', href: '/contact?type=provider' },
  ],
  faq: [
    { q: 'How much does it cost to be listed?',
      a: 'Claiming and maintaining your provider profile is free. CaraLoom monetizes on the hospital / health-system side, not by charging providers for visibility.' },
    { q: 'Do you rank providers by who pays the most?',
      a: 'No. Ranking is driven by verified capabilities (does the patient match your services and payer mix), geography, and objective response signal (acceptance rate, response time). There is no pay-to-play tier.' },
    { q: 'How do referrals reach my team?',
      a: 'You control the intake routing during onboarding — email, phone, secure eFax, or API. Referrals arrive with coverage checked and a structured packet.' },
  ],
  softwareFeatures: [
    'Free provider profile claiming',
    'Verified capability + service-area data',
    'Objective ranking on response signal (no pay-to-play)',
    'Referral packets with coverage pre-checked',
    'Intake routing per organization preference',
    'Business-development dashboard',
  ],
  bottomCtaTitle: 'Claim your CaraLoom provider profile',
  bottomCtaBody: 'Every hospital case manager searching your service area sees your profile. Verify what\u2019s true, own the data, and take referrals with the workflow you already run.',
};

export const ALL_SOLUTIONS_CONFIGS = [
  solutionsHospitalsConfig,
  solutionsHealthSystemsConfig,
  solutionsCaseManagersConfig,
  solutionsPostAcuteProvidersConfig,
];
